import { defineStore } from "pinia";
import { ref, computed } from "vue";
import axios from "@/plugins/axios";
import axiosRaw from "axios";
import { showMessage } from "@/utils/notifications";

export const useFileStore = defineStore("file", () => {
  // State
  const fileList = ref([]);
  const fileDownloadList = ref([]);

  // Getters
  const getFileList = computed(() => fileList.value);
  const getFileDownloadList = computed(() => fileDownloadList.value);

  // Actions
  const setFileList = (data) => {
    fileList.value = data;
  };

  const setFileDownloadList = (data) => {
    fileDownloadList.value = data;
  };

  const addFile = (data) => {
    for (var i = 0; i < fileList.value.length; i++) {
      if (fileList.value[i].entity_id == data.entity_id) {
        fileList.value[i] = data;
        return;
      }
    }
    fileList.value.push(data);
  };

  const addFileDownload = (data) => {
    for (var i = 0; i < fileDownloadList.value.length; i++) {
      if (fileDownloadList.value[i].entity_id == data.entity_id) {
        fileDownloadList.value[i] = data;
        return;
      }
    }
    fileDownloadList.value.push(data);
  };

  const deleteFile = (payload) => {
    let index = fileList.value.findIndex((e) => e.entity_id == payload.entity_id);
    if (index > -1) {
      fileList.value.splice(index, 1);
    }
  };

  // API Actions
  const getFiles = async () => {
    try {
      const response = await axios.get(`/file`);
      const resData = response.data;

      if (!resData.success) {
        showMessage({ ...resData, messageType: "error", messageTitle: "Error" });
        return resData;
      }

      setFileList(resData.files);
      return resData;
    } catch (error) {
      console.error("Error fetching files:", error);
      return { success: false, message: "Failed to fetch files" };
    }
  };

  const getFileDownloads = async () => {
    try {
      const response = await axios.get(`/file_download/me`);
      const resData = response.data;

      if (!resData.success) {
        showMessage({ ...resData, messageType: "error", messageTitle: "Error" });
        return resData;
      }

      setFileDownloadList(resData.file_downloads);
      return resData;
    } catch (error) {
      console.error("Error fetching file downloads:", error);
      return { success: false, message: "Failed to fetch file downloads" };
    }
  };

  const getFilesForInvestor = async (investor_id) => {
    try {
      const response = await axios.get(`/file/investor/${investor_id}`);
      const resData = response.data;

      if (!resData.success) {
        showMessage({ ...resData, messageType: "error", messageTitle: "Error" });
        return null;
      }

      return resData.files;
    } catch (error) {
      console.error("Error fetching files for investor:", error);
      return null;
    }
  };

  const getFilesForInvestment = async (investment_id) => {
    try {
      const response = await axios.get(`/file/investment/${investment_id}`);
      const resData = response.data;

      if (!resData.success) {
        showMessage({ ...resData, messageType: "error", messageTitle: "Error" });
        return null;
      }

      return resData.files;
    } catch (error) {
      console.error("Error fetching files for investment:", error);
      return null;
    }
  };

  const sendFileToBackend = async (payload) => {
    try {
      const { data } = await axios.post(`/file`, payload);

      if (!data.success) {
        showMessage({ ...data, messageType: "error", messageTitle: "Error" });
        return data;
      }

      showMessage({ ...data, messageType: "success", messageTitle: "Success" });
      addFile(data.file);
      return true;
    } catch (error) {
      console.error("Error sending file to backend:", error);
      return { success: false, message: "Failed to send file to backend" };
    }
  };

  const getEmailRecipients = async (payload) => {
    try {
      const { data } = await axios.post(`/get-email-recipients`, payload);
      return data;
    } catch (error) {
      console.error("Failed to get email recipients:", error);
      return { success: false, message: "Failed to get email recipients" };
    }
  };

  const deleteFileAction = async (payload) => {
    try {
      const { data } = await axios.delete(`/file`, {
        data: { entity_id: payload.entity_id },
      });

      if (!data.success) {
        showMessage({ ...data, messageType: "error", messageTitle: "Error" });
        return data;
      }

      showMessage({ ...data, messageType: "success", messageTitle: "Success" });
      deleteFile(payload);
      return true;
    } catch (error) {
      console.error("Error deleting file:", error);
      return { success: false, message: "Failed to delete file" };
    }
  };

  const sendFileDownloadEvent = async (payload) => {
    try {
      const { data } = await axios.post(`/file_download`, {
        file_id: payload.entity_id,
        file_url: payload.file_url,
        file_name: payload.file_name,
      });

      if (!data.success) {
        showMessage({ ...data, messageType: "error", messageTitle: "Error" });
        return data;
      }

      showMessage({ ...data, messageType: "success", messageTitle: "Success" });
      addFileDownload(data.file_download);
      return true;
    } catch (error) {
      console.error("Error sending file download event:", error);
      return { success: false, message: "Failed to send file download event" };
    }
  };

  const getFileDownloadEvents = async () => {
    try {
      const response = await axios.get(`/file_download_events`);
      const resData = response.data;

      if (!resData.success) {
        showMessage({ ...resData, messageType: "error", messageTitle: "Error" });
        return resData;
      }

      return resData;
    } catch (error) {
      console.error("Error fetching file download events:", error);
      return { success: false, message: "Failed to fetch file download events" };
    }
  };

  const getFileDownloadEventsList = async (pageOptions) => {
    try {
      const params = new URLSearchParams();

      if (pageOptions?.page) {
        params.append("page", pageOptions.page);
      }
      if (pageOptions?.itemsPerPage) {
        params.append("itemsPerPage", pageOptions.itemsPerPage);
      }
      if (pageOptions?.search) {
        params.append("search", pageOptions.search);
      }

      if (pageOptions && Array.isArray(pageOptions.sortBy) && pageOptions.sortBy.length > 0) {
        params.append("sort_key", pageOptions.sortBy[0].key);
        params.append("sort_order", pageOptions.sortBy[0].order);
      }

      const response = await axios.get(`/file_download_eventslist`, { params });
      const resData = response.data;

      if (!resData.success) {
        showMessage({ ...resData, messageType: "error", messageTitle: "Error" });
        return resData;
      }

      return resData;
    } catch (error) {
      console.error("Error fetching file download events list:", error);
      return { success: false, message: "Failed to fetch file download events list" };
    }
  };

  const downloadFileStackFile = async (file) => {
    const { file_name, filename, name } = file;
    const finalFilename = file_name || filename || name || "download";

    try {
      const handle = file.file_url.split("/").pop();

      if (!handle) {
        throw new Error("Error while downloading file");
      }

      const response = await axiosRaw.get(`https://www.filestackapi.com/api/file/${handle}`, {
        responseType: "blob",
      });

      const downloadLink = document.createElement("a");
      downloadLink.href = URL.createObjectURL(new Blob([response.data]));
      downloadLink.download = finalFilename;

      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      // Log the download event
      await sendFileDownloadEvent(file);
      return response;
    } catch (error) {
      console.error("Error downloading file:", error);
      showMessage({
        message: "Failed to download file",
        messageType: "error",
        messageTitle: "Download Error",
      });
      throw error;
    }
  };

  return {
    // State
    fileList,
    fileDownloadList,

    // Getters
    getFileList,
    getFileDownloadList,

    // Actions
    setFileList,
    setFileDownloadList,
    addFile,
    addFileDownload,
    deleteFile,

    // API Actions
    getFiles,
    getFileDownloads,
    getFilesForInvestor,
    getFilesForInvestment,
    sendFileToBackend,
    getEmailRecipients,
    deleteFileAction,
    sendFileDownloadEvent,
    getFileDownloadEvents,
    getFileDownloadEventsList,
    downloadFileStackFile,
  };
});
